import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import { removeByIdDialogMutation } from '~/shared/graphql-queries';
import createOneDialogMutation from '~/shared/graphql-queries/dialogs/create-one-dialog-mutation';
import dialogFragment from '~/shared/graphql-queries/dialogs/dialog-fragment';
import unreadMessagesCountQuery from '~/shared/graphql-queries/dialogs/unread-messages-count-query';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationDialogResponse = Readonly<{
  data?: {
    paginationDialogQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type UnreadMessagesCountResponse = Readonly<{
  data?: {
    unreadMessagesCountQuery?: number;
  };
}>;

type UnreadMessagesCountArgs = Readonly<{
  dialogUsers?: string[];
  course?: string;
  lesson?: string;
  isCommonDialog?: boolean;
}>;

type PaginationDialogResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationDialogArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type RemoveByIdDialogArgs = Readonly<{
  _id?: string;
}>;

type CreateOneDialogArgs = Readonly<{
  type?: string;
  course?: string;
  lesson?: string;
  courseCategory?: string;
  dialogUsers?: unknown[];
  isCommonDialog?: boolean;
}>;

const transformDialogResponse =
  (type: 'paginationDialogQuery') =>
  (response: PaginationDialogResponse): PaginationDialogResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const dialogsTagTypes = ['dialogs'] as const;

export const getDialogsQuery = (builder: BuilderType) => {
  return {
    paginationDialog: builder.query<PaginationDialogResult, PaginationDialogArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'dialog',
              fragment: dialogFragment,
              filterType: 'FilterFindManyDialogWithUsersInput',
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_DESC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['dialogs'],
      transformResponse: transformDialogResponse('paginationDialogQuery'),
    }),

    removeByIdDialog: builder.mutation<unknown, RemoveByIdDialogArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdDialogMutation),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),
    unreadMessagesCount: builder.query<number, UnreadMessagesCountArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(unreadMessagesCountQuery),
          variables: {
            dialogUsers: args?.dialogUsers,
            dialog: args?.dialog,
            course: args?.course,
            lesson: args?.lesson,
            isCommonDialog: args?.isCommonDialog,
          },
        }),
      }),
      providesTags: ['dialogs'],
      transformResponse: (response: UnreadMessagesCountResponse) => {
        return response.data?.unreadMessagesCountQuery ?? 0;
      },
    }),
    createOneDialog: builder.mutation<unknown, CreateOneDialogArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneDialogMutation),
          variables: {
            type: args?.type,
            course: args?.course,
            lesson: args?.lesson,
            courseCategory: args?.courseCategory,
            dialogUsers: args?.dialogUsers,
            isCommonDialog: args?.isCommonDialog,
          },
        }),
      }),
    }),
  };
};
